
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MediaPickerModal } from "@/components/email-builder/MediaPickerModal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ImageBlockConfiguration } from "@/types/block-configurations";
import {
  AlignmentControls,
  BorderControls,
  PaddingControls,
  LinkControls,
  ResponsiveControls,
} from "../shared";

interface ImageBlockPropertiesProps {
  config: ImageBlockConfiguration;
  onChange: (config: ImageBlockConfiguration) => void;
}

/**
 * ImageBlockProperties Component
 * 
 * Property panel for image blocks with source, alt text, dimensions, and styling.
 * 
 * @param config - Current image block configuration
 * @param onChange - Callback when configuration changes
 */
export function ImageBlockProperties({ config, onChange }: ImageBlockPropertiesProps) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const updateField = <K extends keyof ImageBlockConfiguration>(
    field: K,
    value: ImageBlockConfiguration[K]
  ) => {
    onChange({ ...config, [field]: value });
  };

  const handleMediaSelect = (url: string) => {
    updateField('src', url);
  };

  return (
    <div className="space-y-3">
      {/* Thumbnail preview */}
      {config.src && (
        <div className="rounded-md overflow-hidden border border-border bg-muted/50">
          <img
            src={config.src}
            alt={config.alt}
            className="w-full h-28 object-contain"
          />
        </div>
      )}

      {/* Primary Fields */}
      <div>
        <Label className="text-xs font-medium mb-1 block">
          Image URL <span className="text-destructive">*</span>
        </Label>
        <div className="flex items-center gap-1.5">
          <Input
            type="url"
            value={config.src}
            onChange={(e) => updateField('src', e.target.value)}
            placeholder="https://example.com/image.jpg"
            required
            className="flex-1 min-w-0"
          />
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="shrink-0 rounded-md border border-input bg-background p-2 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            title="Pick from media library"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </button>
        </div>
      </div>

      <div>
        <Label className="text-xs font-medium mb-1 block">
          Alt Text <span className="text-destructive">*</span>
        </Label>
        <Input
          type="text"
          value={config.alt}
          onChange={(e) => updateField('alt', e.target.value)}
          placeholder="Describe the image"
          maxLength={200}
          required
        />
        <p className="text-xs text-muted-foreground mt-1">
          Required for accessibility (max 200 chars)
        </p>
      </div>

      {/* Dimensions */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-medium mb-1 block">Width (px)</Label>
          <Input
            type="number"
            value={config.width ?? ''}
            onChange={(e) => updateField('width', e.target.value ? parseInt(e.target.value) : undefined)}
            placeholder="Auto"
            min={50}
            max={1200}
          />
        </div>
        <div>
          <Label className="text-xs font-medium mb-1 block">Height (px)</Label>
          <Input
            type="number"
            value={config.height ?? ''}
            onChange={(e) => updateField('height', e.target.value ? parseInt(e.target.value) : undefined)}
            placeholder="Auto"
            min={50}
            max={1200}
          />
        </div>
      </div>

      {/* Collapsible Sections */}
      <Accordion type="multiple" defaultValue={["alignment"]} className="w-full">
        
        {/* Alignment Section */}
        <AccordionItem value="alignment">
          <AccordionTrigger>Alignment</AccordionTrigger>
          <AccordionContent>
            <AlignmentControls
              value={config.alignment}
              onChange={(alignment) => updateField('alignment', alignment)}
              showVertical={false}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Border Section */}
        <AccordionItem value="border">
          <AccordionTrigger>Border & Radius</AccordionTrigger>
          <AccordionContent>
            <BorderControls
              value={config.border}
              onChange={(border) => updateField('border', border)}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Spacing Section */}
        <AccordionItem value="spacing">
          <AccordionTrigger>Spacing</AccordionTrigger>
          <AccordionContent>
            <PaddingControls
              value={config.padding}
              onChange={(padding) => updateField('padding', padding)}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Link Section */}
        <AccordionItem value="link">
          <AccordionTrigger>Link</AccordionTrigger>
          <AccordionContent>
            <LinkControls
              value={config.link}
              onChange={(link) => updateField('link', link)}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Responsive Section */}
        <AccordionItem value="responsive">
          <AccordionTrigger>Responsive</AccordionTrigger>
          <AccordionContent>
            <ResponsiveControls
              value={config.responsive}
              onChange={(responsive) => updateField('responsive', responsive)}
            />
            <div className="mt-3 flex items-center gap-2">
              <input
                type="checkbox"
                id="fluid-on-mobile"
                checked={config.fluidOnMobile ?? false}
                onChange={(e) => updateField('fluidOnMobile', e.target.checked)}
                className="w-4 h-4 rounded border-input"
              />
              <Label htmlFor="fluid-on-mobile" className="text-xs font-medium cursor-pointer">
                Full width on mobile
              </Label>
            </div>
          </AccordionContent>
        </AccordionItem>

      </Accordion>

      <MediaPickerModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handleMediaSelect}
      />
    </div>
  );
}
